import { t } from "@travily/i18n";
import { ValidationIcon } from "@ui/components/internal/ValidationIcon";
import { resolveValidationState, type ValidationState } from "@ui/components/shared/resolveValidationState";
import { composeRenderProps } from "@ui/primitives/shared";
import {
  FieldErrorContext,
  TextFieldError as RACFieldError,
  TextFieldGroup as RACGroup,
  TextFieldLabel as RACLabel,
  TextFieldText as RACText,
} from "@ui/primitives/TextField";
import { createContext, type ReactNode, use, useContext, useId, useMemo } from "react";
import { type FieldSize, field } from "./field.styles";

export interface FieldContextValue {
  size: FieldSize;
  isOptional: boolean;
  isRequired: boolean;
  isDisabled: boolean;
  isInvalid: boolean;
  labelId: string;
  descriptionId: string;
  errorId: string;
}

const FieldContext = createContext<FieldContextValue | null>(null);

function useFieldStrict(componentName: string): FieldContextValue {
  const ctx = use(FieldContext);
  if (!ctx) throw new Error(`${componentName} must be used within a Field`);
  return ctx;
}

/** Read the surrounding Field's state. Returns null when used outside a Field.
 *  Non-RAC widgets (e.g. RatingInput) can read `labelId` and pass it as `aria-labelledby`. */
export function useField(): FieldContextValue | null {
  return use(FieldContext);
}

export interface FieldProps {
  children: ReactNode;
  className?: string;
  size?: FieldSize;
  /** Appends a styled "(optional)" hint to the label */
  isOptional?: boolean;
  /** Appends a styled "(required)" hint to the label */
  isRequired?: boolean;
  /** Disables the field visually; consumers must also pass to inner control */
  isDisabled?: boolean;
  /** Switches Field.Control to its invalid styling when no explicit validationState is set */
  isInvalid?: boolean;
}

function FieldRoot({
  children,
  className,
  size = "lg",
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  isInvalid = false,
}: FieldProps) {
  const labelId = useId();
  const descriptionId = useId();
  const errorId = useId();
  const styles = field({ size, isDisabled });
  const value = useMemo<FieldContextValue>(
    () => ({ size, isOptional, isRequired, isDisabled, isInvalid, labelId, descriptionId, errorId }),
    [size, isOptional, isRequired, isDisabled, isInvalid, labelId, descriptionId, errorId],
  );
  return (
    <FieldContext value={value}>
      <div className={styles.root({ className })}>{children}</div>
    </FieldContext>
  );
}

export interface FieldLabelProps {
  children: ReactNode;
  className?: string;
  /** Visually hides the label while keeping it accessible to screen readers */
  isHidden?: boolean;
}

function FieldLabel({ children, className, isHidden }: FieldLabelProps) {
  const ctx = useFieldStrict("Field.Label");
  const styles = field({ size: ctx.size, isLabelHidden: isHidden });
  const suffix = ctx.isOptional
    ? t("components.field.optional")
    : ctx.isRequired
      ? t("components.field.required")
      : null;
  return (
    <RACLabel className={styles.label({ className })} id={ctx.labelId}>
      {children}
      {suffix && <span className={styles.optional()}>{suffix}</span>}
    </RACLabel>
  );
}

export interface FieldDescriptionProps {
  children: ReactNode;
  className?: string;
}

function FieldDescription({ children, className }: FieldDescriptionProps) {
  const ctx = useFieldStrict("Field.Description");
  const styles = field({ size: ctx.size });
  return (
    <RACText className={styles.description({ className })} id={ctx.descriptionId} slot="description">
      {children}
    </RACText>
  );
}

function FieldSuccess({ children, className }: FieldDescriptionProps) {
  const ctx = useFieldStrict("Field.Success");
  const styles = field({ size: ctx.size });
  return (
    <RACText className={styles.success({ className })} id={ctx.descriptionId} slot="description">
      {children}
    </RACText>
  );
}

export interface FieldErrorProps {
  children?: ReactNode;
  className?: string;
}

function FieldError({ children, className }: FieldErrorProps) {
  const ctx = useFieldStrict("Field.Error");
  const styles = field({ size: ctx.size });
  // Detect whether we're nested in a RAC field root (TextField, SearchField, etc.).
  // RAC sets FieldErrorContext's default to `null` and only provides a non-null value
  // when a field root is present. If RAC ever changes that default, the truthy check
  // (rather than `!== null`) keeps the dual-mode fallback working.
  const racErrorContext = useContext(FieldErrorContext);

  if (racErrorContext) {
    return (
      <RACFieldError className={styles.error({ className })} id={ctx.errorId}>
        {children}
      </RACFieldError>
    );
  }
  return (
    <p className={styles.error({ className })} id={ctx.errorId} role="alert">
      {children}
    </p>
  );
}

export interface FieldControlProps {
  children: ReactNode;
  className?: string;
  /** Wrap children in a React Aria <Group> for focus-within tracking. Use for RAC field roots that expect a Group. */
  asGroup?: boolean;
  leadIcon?: ReactNode;
  /** Rendered after the validation icon, inside the control (e.g. a picker's popover-toggle button). */
  suffix?: ReactNode;
  /** Cross-axis alignment of the control's contents. Use "start" for multi-line controls
   *  (textareas) so the lead/validation icons pin to the top instead of centering. */
  align?: "center" | "start";
  /** Override automatic invalid state from Field's `isInvalid`. Use to opt into the success state. */
  validationState?: ValidationState;
}

function FieldControl({
  children,
  className,
  asGroup,
  leadIcon,
  suffix,
  align = "center",
  validationState,
}: FieldControlProps) {
  const ctx = useFieldStrict("Field.Control");
  const effectiveValidation = validationState ?? resolveValidationState(ctx.isInvalid, null);
  const alignVariant = align === "start" ? "start" : undefined;
  const styles = field({ size: ctx.size, align: alignVariant, validationState: effectiveValidation });

  const inner = (
    <>
      {leadIcon && (
        <span aria-hidden="true" className={styles.leadIcon()}>
          {leadIcon}
        </span>
      )}
      {children}
      {effectiveValidation && <ValidationIcon className={styles.validationIcon()} state={effectiveValidation} />}
      {suffix}
    </>
  );

  if (asGroup) {
    return (
      <RACGroup
        className={composeRenderProps(className, (cn, { isFocusWithin }) =>
          field({
            size: ctx.size,
            align: alignVariant,
            validationState: effectiveValidation,
            isFocused: isFocusWithin && !effectiveValidation,
          }).control({ className: cn }),
        )}
      >
        {inner}
      </RACGroup>
    );
  }

  return <div className={styles.control({ className })}>{inner}</div>;
}

export const Field = Object.assign(FieldRoot, {
  Label: FieldLabel,
  Description: FieldDescription,
  Success: FieldSuccess,
  Error: FieldError,
  Control: FieldControl,
});
